/* INP R2d (#27): IO-gated lazy Swiper for the below-fold testimonial
   sections (ss-testimonial-12 / ss-testimonials-38 / ss-testimonials-42).
   Each section registers { root, init, js, css, deps } on window.SSLazySwiper;
   this loader waits until a root comes within 600px of the viewport, then
   loads ONE deduped Swiper 11 build — the self-hosted theme assets —
   reusing an existing window.Swiper v11 global or swiper stylesheet when
   another section (hero, circle-menu) already put one on the page — and
   finally calls the section's init with the resolved constructor. The
   sections keep their `.preview` fallback CSS so the un-initialized state
   renders as intentional static slides, and their `.swiper` root class so
   the theme's .cv-late lock keeps skipping them (R1 cv-vs-swiper trap). */

window.SSLazySwiper = window.SSLazySwiper || { q: [] };

(function () {
  var api = window.SSLazySwiper;
  var APPROACH_MARGIN = '600px 0px';

  // Dedupe state lives on the page, not in this closure: a second execution
  // of this file (duplicate script tag) would otherwise get fresh flags and
  // re-inject the whole build (step-5 review finding).
  var state = (window.__ssSwiperLazyState = window.__ssSwiperLazyState || {
    chain: Promise.resolve(),
    injectedJs: false,
    injectedCss: false,
    injectedDeps: {}
  });
  var io = null;

  function existingSwiper() {
    // The v11 bundle exposes no static Swiper.version, so a version check is
    // impossible — but after this change every Swiper source on the store is
    // v11 (hero/circle-menu/vso jsdelivr, collections-menu self-hosted), so
    // an existing global is by construction the right build to reuse.
    // REVIEWER NOTE (deliberate coupling): this reuse assumes ss-hero-32's
    // and ss-circle-menu's jsdelivr tags stay parser-blocking (no defer/
    // async) — they have settled before any deferred script runs. If they
    // ever gain defer/async, an IO callback could beat the eager build and
    // fetch this byte-identical self-hosted bundle a second time — wasted
    // bytes, never a version mismatch.
    return typeof window.Swiper === 'function';
  }

  function existingCss() {
    // link.sheet is null for a stylesheet that failed or was blocked — at
    // the load+500ms observation gate every stylesheet has settled, so
    // require an APPLIED stylesheet, not a present tag (mimo review
    // finding: under a jsdelivr-blocking ad blocker the dead hero <link>
    // must not suppress the self-hosted CSS injection).
    var links = document.querySelectorAll('link[rel="stylesheet"][href*="swiper-bundle"]');
    for (var i = 0; i < links.length; i++) {
      if (links[i].sheet) return true;
    }
    return false;
  }

  function injectScript(src) {
    return new Promise(function (done) {
      var el = document.createElement('script');
      el.src = src;
      el.onload = done;
      el.onerror = done;
      document.head.appendChild(el);
    });
  }

  function injectCss(href) {
    return new Promise(function (done) {
      var el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.onload = done;
      el.onerror = done;
      document.head.appendChild(el);
    });
  }

  // One shared pipeline for the whole page: whichever lazy section
  // approaches first resolves the build for all of them. Per-section deps
  // (vanilla-lazyload for #12) join the same chain, so init is guaranteed
  // to run after its library — the old sync <script> ordering, recreated.
  // REVIEWER NOTE (deliberate, re-flagged twice): onerror RESOLVES on
  // purpose and the injected-flag is never reset — no retry path. A
  // rejected chain would silently skip every later section's init (the
  // one hazard this resolve-on-error design makes unreachable), and the
  // assets are same-origin Shopify CDN (a 404 is a deploy mismatch no
  // retry can fix). Retry-on-init-failure is also blocked by the
  // dataset flag below (a throwing init must not be retried into a
  // double construct on a half-built loop root). The failure paths
  // then differ, on purpose: a failed JS bundle leaves the section in
  // its static .preview state with runInit's "Swiper failed to load"
  // warn, while a failed CSS request resolves like a success — init
  // runs whenever window.Swiper exists, silently, yielding an
  // initialized but unstyled slider (the one thing the .preview CSS
  // cannot substitute for). Observable outcomes, spelled out for reviewers:
  // failed Swiper JS => runInit's guard SKIPS init entirely with a warn
  // (sections stay static); failed dep (vanilla-lazyload) => init still
  // runs and #12's `new LazyLoad()` throw is caught + warned here — the
  // slider keeps working, only lazy media is lost; failed CSS => init
  // runs silently, unstyled; ANY failure leaves sibling registrations
  // working because the chain never rejects. Rejecting instead would
  // silently kill every later section's init.
  function ensureBuild(cfg) {
    if (!state.injectedJs && !existingSwiper() && cfg.js) {
      state.injectedJs = true;
      state.chain = state.chain.then(function () {
        return injectScript(cfg.js);
      });
    }
    if (!state.injectedCss && !existingCss() && cfg.css) {
      state.injectedCss = true;
      state.chain = state.chain.then(function () {
        return injectCss(cfg.css);
      });
    }
    (cfg.deps || []).forEach(function (url) {
      if (state.injectedDeps[url]) return;
      state.injectedDeps[url] = true;
      state.chain = state.chain.then(function () {
        return injectScript(url);
      });
    });
    return state.chain;
  }

  function runInit(cfg) {
    ensureBuild(cfg).then(function () {
      if (!window.Swiper) {
        console.warn('[swiper-lazy] Swiper failed to load for', cfg.root);
        return;
      }
      // query at init time: in the theme editor the section DOM may have
      // been replaced between the IO callback and the build resolving
      var root = document.querySelector(cfg.root);
      if (!root || root.dataset.ssSwiperInit) return;
      // REVIEWER NOTE (deliberate ordering, flagged by 3 review lanes): the
      // flag is set BEFORE init on purpose — a throwing init cannot be
      // retried into a second `new Swiper` on a half-built loop root (loop
      // duplicates slides on re-init). An init that throws mid-way cannot
      // be retried and the section keeps whatever state it reached (e.g.
      // #12 removes .preview first); the warn below is the only signal.
      root.dataset.ssSwiperInit = '1';
      try {
        cfg.init(window.Swiper);
      } catch (err) {
        console.warn('[swiper-lazy] init failed for', cfg.root, err);
      }
    });
  }

  function watch(cfg) {
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // isolate entries: a throw on one must not abort the batch and
            // lose the already-unobserved siblings (muse review finding)
            try {
              if (!entry.isIntersecting) return;
              io.unobserve(entry.target);
              runInit(entry.target.__ssLazySwiperCfg);
            } catch (err) {
              console.warn('[swiper-lazy] observer entry failed', err);
            }
          });
        },
        { rootMargin: APPROACH_MARGIN }
      );
    }
    cfg.el.__ssLazySwiperCfg = cfg;
    io.observe(cfg.el);
  }

  var pending = [];
  var watchOpen = false;

  function register(cfg) {
    if (!cfg || !cfg.root || typeof cfg.init !== 'function') return;
    var root = document.querySelector(cfg.root);
    if (!root) return; // section not on this page
    cfg.el = root;

    // Theme editor: initialize immediately — editors must see live sliders,
    // and re-rendered sections re-register through their inline scripts.
    if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
      runInit(cfg);
      return;
    }

    // Below-fold sections drift down by hundreds of px while late images and
    // app content expand the page (measured: a root 23px inside the 600px
    // margin at parse time settled 516px outside) — observing at parse-time
    // geometry fires early and loads the build pre-load-event. Gate the
    // observers on the load event + 500ms settle, same lesson as the
    // theme's .cv-late lock (theme.liquid lockBelowFold).
    if (watchOpen) {
      watch(cfg);
    } else {
      pending.push(cfg);
    }
  }

  function openWatch() {
    watchOpen = true;
    pending.splice(0).forEach(watch);
  }

  if (document.readyState === 'complete') {
    setTimeout(openWatch, 500);
  } else {
    window.addEventListener('load', function () {
      setTimeout(openWatch, 500);
    }, { once: true });
  }

  api.register = register;
  // a third party may have pre-defined window.SSLazySwiper without a queue
  api.q = api.q || [];
  while (api.q.length) register(api.q.shift());
})();
